import { firebaseAction } from '../utils'
import { userSelector, authSelector } from '../selectors/baseSelectors'
import getUrl from '../../utils/getUrl'
import { OWNER, VIEWER, CREATED } from '../../constants'
import { newBoardSectionHeight } from 'variables'
import ReactGA from 'react-ga'
import { push } from 'react-router-redux'
import randomColor from '../../utils/randomColor'
import i18n from 'i18n'
import firebase from 'firebase/app'
import {
  SET_ZOOM,
  SET_TEXT_EDIT_MODE,
  SET_PLACEMENT_MODE,
  SET_PLACEMENT_POSITION,
  SET_BOARD_SIZE,
  PLACE_BLOCK,
  TOGGLE_GRID_HIDE,
  SET_MODE
} from '../actionTypes'

import { LOCATION_CHANGE } from 'react-router-redux'
import { screenSizeSelector } from '../selectors/boardSelectors'
import {
  accessListSelector,
  accessLevelSelector
} from '../selectors/accessSelectors'
import {
  hideSidebar,
  setEditingItem,
  setSidebarFilter
} from './sidebar'
import { confirm } from './alerts'
import { setMode } from './app'

const timestamp = firebase.database.ServerValue.TIMESTAMP

export const setZoom = zoom => ({ type: SET_ZOOM, payload: { zoom } })

export const setTextEditMode = id => ({
  type: SET_TEXT_EDIT_MODE,
  payload: { id }
})
export const toggleGridHide = id => ({
  type: TOGGLE_GRID_HIDE
})
export const setPlacementMode = mode => ({
  type: SET_PLACEMENT_MODE,
  payload: { mode }
})
export const setPlacementPosition = position => ({
  type: SET_PLACEMENT_POSITION,
  payload: { position }
})
export const setBoardSize = size => ({
  type: SET_BOARD_SIZE,
  payload: { size }
})

const initialState = {
  textEditMode: null,
  placementMode: null,
  size: { width: window.innerWidth, height: window.innerHeight },
  zoom: 1
}
export const boardReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case LOCATION_CHANGE:
      return {
        ...state,
        textEditMode: false, // exit text-edit mode
        placementMode: null
      }
    case SET_MODE:
      return { ...state, placementMode: null }
    case SET_TEXT_EDIT_MODE:
      return { ...state, textEditMode: payload.id }
    case TOGGLE_GRID_HIDE:
      return { ...state, hideGrid: !state.hideGrid }
    case PLACE_BLOCK:
      if (payload.block.text !== undefined) {
        return { ...state, textEditMode: payload.block.id }
      }
      return state
    case SET_PLACEMENT_MODE:
      return { ...state, placementMode: payload.mode }
    case SET_PLACEMENT_POSITION:
      return { ...state, placementPosition: payload.position }
    case SET_ZOOM:
      return { ...state, zoom: payload.zoom }
    case SET_BOARD_SIZE:
      return { ...state, size: payload.size }
    default:
      return state
  }
}

export const goToBoard = board => (dispatch, getState) => {
  dispatch(push(getUrl(board)))

  // close Sidebar on small screens (it takes up whole screen)
  if (screenSizeSelector(getState()).sizeClass === 'small') {
    dispatch(hideSidebar())
  }
}

export const createBoard = (parent) =>
  firebaseAction((update, { dispatch, getState }) => {
    const state = getState()
    const user = userSelector(state)

    const id = firebase.ref().push().key
    const name = i18n.t('globalNav.newBoardName')
    const color = randomColor(name)
    const board = {
      id,
      name,
      dateCreated: timestamp,
      owner: {
        id: user.uid,
        displayName: user.displayName
      },
      color,
      type: 'board',
      viewLinkKey: 'open'
    }
    const sectionId = firebase
      .database()
      .ref()
      .push().key
    const forward = {
      [`/boards/${id}`]: board,
      [`/sections/${id}/${sectionId}`]: {
        board: id,
        id: sectionId,
        height: newBoardSectionHeight,
        number: 0
      },
      [`/access/${user.uid}/${board.id}`]: {
        id,
        name,
        color,
        isOwner: true,
        // large number will be top (order is descending), increase with date so newset is first
        order: new Date().valueOf() - 1494110302577,
        type: 'board',
        accessLevel: OWNER,
        dateCreated: timestamp,
        dateLastViewed: timestamp
      },
      [`/shares/${board.id}/open`]: {
        date: timestamp,
        id: 'open',
        item: {
          id,
          name,
          type: 'board'
        },
        accessLevel: VIEWER,
        multiUse: true
      }
    }

    if (parent) {
      parent = accessListSelector(state)[parent]
      forward[`/folders/${parent.id}/children/${board.id}`] = {
        id: board.id,
        name: board.name,
        type: 'board'
      }
      forward[`/boards/${id}`].color = parent.color
      forward[`/access/${user.uid}/${board.id}`].color = parent.color
      forward[`/access/${user.uid}/${board.id}`].parent = parent.id
      board.folders = []
      while (parent) {
        board.folders.push({
          id: parent.id,
          name: parent.name,
          color: parent.color
        })
        parent = parent.parent && accessListSelector(state)[parent.parent]
      }
    }

    return update({ forward }).then(() => {
      dispatch(setSidebarFilter(CREATED))
      dispatch(push(getUrl(board)))
      if (state.app.hasMouse) dispatch(setMode('edit'))
      dispatch(setEditingItem(board.id))
      ReactGA.event({ category: 'Board', action: 'Create Board' }, ['app'])
    })
  })

export const duplicateBoard = existingId =>
  firebaseAction(async (update, { dispatch, getState, getOnce, getKey }) => {
    const [existingBoard, existingSections, existingBlocks] = await Promise.all([
      getOnce(`/boards/${existingId}`),
      getOnce(`/sections/${existingId}`),
      getOnce(`/blocks/${existingId}`)
    ])

    const state = getState()
    const user = userSelector(state)

    const board = {
      id: getKey(),
      name: existingBoard.name + i18n.t('globalNav.duplicateIndicator'),
      dateCreated: timestamp,
      owner: {
        id: user.uid,
        displayName: user.displayName
      },
      color: existingBoard.color,
      type: 'board',
      viewLinkKey: 'open'
    }
    const sections = {}
    const newSectionIds = {}
    Object.keys(existingSections).forEach(sectionId => {
      let section = existingSections[sectionId]
      const id = getKey()
      newSectionIds[sectionId] = id
      sections[id] = Object.assign(section, {
        board: board.id,
        id
      })
    })
    const blocks = {}
    Object.keys(existingBlocks).forEach(blockId => {
      let block = existingBlocks[blockId]
      const id = getKey()
      blocks[id] = Object.assign(block, {
        board: board.id,
        section: newSectionIds[block.section],
        id,
        owner: user.uid
      })
    })
    const forward = {
      [`/boards/${board.id}`]: board,
      // add the sections
      [`/sections/${board.id}`]: sections,
      // add the blocks
      [`/blocks/${board.id}`]: blocks,
      [`/access/${user.uid}/${board.id}`]: {
        id: board.id,
        name: board.name,
        color: board.color,
        isOwner: true,
        // large number will be top (order is descending), increase with date so newset is first
        order: new Date().valueOf() - 1494110302577,
        type: 'board',
        accessLevel: OWNER,
        dateCreated: timestamp,
        dateLastViewed: timestamp
      },
      [`/shares/${board.id}/open`]: {
        date: timestamp,
        id: 'open',
        item: {
          id: board.id,
          name: board.name,
          type: 'board'
        },
        accessLevel: VIEWER,
        multiUse: true
      }
    }

    return update({ forward }).then(() => {
      dispatch(push(getUrl(board)))
      if (state.app.hasMouse) dispatch(setMode('edit'))
      dispatch(setEditingItem(board.id))
      ReactGA.event({ category: 'Board', action: 'Duplicate Board' }, ['app'])
    })
  })

export const updateViewDate = boardId =>
  firebaseAction((update, { getState }) => {
    const state = getState()
    const auth = authSelector(state)
    const access = accessLevelSelector(state)
    if (!auth.isAnonymous && access) {
      const forward = {
        [`/access/${auth.uid}/${boardId}/dateLastViewed`]: timestamp
      }
      return update({ forward })
    }
  })

export const updateBoard = (board, data) =>
  firebaseAction((update, { getAuth }) => {
    const forward = {}
    Object.keys(data).forEach(key => {
      forward[`/boards/${board.id}/${key}`] = data[key]
    })

    // update my own accessList to make interface update faster
    const treePath = `/access/${getAuth().uid}/${board.id}/`
    if ('name' in data) {
      forward[treePath + 'name/'] = data.name
    }
    if ('color' in data) {
      forward[treePath + 'color/'] = data.color
    }

    return update({ forward })
  })

export const archiveBoard = board =>
  firebaseAction((update, { getAuth, dispatch }) => {
    const action = () => {
      const auth = getAuth()
      ReactGA.event({ category: 'Board', action: 'Archive Board' }, ['app'])
      return update({
        forward: {
          [`/boards/${board.id}/dateArchived`]: timestamp,
          // remove from my accessList for faster UI update
          [`/access/${auth.uid}/${board.id}/dateArchived`]: timestamp
        }
      })
    }

    dispatch(confirm('globalNav.confirmArchive', action))
  })

export const unarchiveBoard = board =>
  firebaseAction((update, { getAuth, getState }) => {
    const auth = getAuth()
    const forward = {
      [`/boards/${board.id}/dateArchived`]: null,
      // remove from my accessList for faster UI update
      [`/access/${auth.uid}/${board.id}/dateArchived`]: null
    }
    if (board.parent) {
      const accessList = accessListSelector(getState())
      const parent = accessList[board.parent]
      if (!parent || parent.dateArchived)
        forward[`/boards/${board.id}/folders`] = null
    }
    ReactGA.event({ category: 'Board', action: 'Archive Board' }, ['app'])
    return update({ forward })
  })

export const deleteBoard = board =>
  firebaseAction((update, { getAuth, getState, dispatch }) => {
    const auth = getAuth()
    ReactGA.event({ category: 'Board', action: 'Delete Board' }, ['app'])
    const forward = {
      [`/boards/${board.id}`]: null,
      // delete from accessList for faster UI update
      [`/access/${auth.uid}/${board.id}`]: null
    }
    if (board.folders) {
      forward[`/folders/${board.folders[0].id}/children/${board.id}`] = null
    }
    // TODO - itterate blocks removing any pending uploads from location
    return (
      update({ forward })
        // navigate away from this board // FIXME - only if needed & go somewhere useful
        .then(() => dispatch(push(`/`)))
    )
  })
